<template>
	<div>
		<template v-if="isClient && newApplication === false && !addingNew">
			<p class="mb-3">
				You can request to add an additional insured to your existing coverage. Upon approval, you
				will receive a notification that your certificates are ready to download.
			</p>
			<p class="mb-3">
				For changing existing records, please <a href="https://www.o2sportsinsurance.com/contact-us/" target="_blank">contact us</a>
				or give us a call: 1-855-351-0202
			</p>
			<hr />
		</template>

		<div v-if="! length(application) && ! length(additionallyInsured)">
			<b-form @submit.prevent>
				<b-form-group
					label="Related Application"
					label-for="application"
					description="Only applications that have been completed and bound may be selected here.">
					<v-select v-model="selectedApplication"
						name="application"
						placeholder="Type to search for an application..."
						:filterable="false"
						:options="applications"
						@search="onAppSearch">
						<template slot="option" slot-scope="option">
							<div class="d-center">
								{{ option.app_id }} - {{ option.name_of_insured }} - {{ option.start_date }}
							</div>
						</template>
						<template slot="selected-option" slot-scope="option">
							<div v-if="length(option)" class="selected d-center">
								{{ option.app_id }}
								({{ option.name_of_insured }})
							</div>
							<div v-else>--</div>
						</template>
					</v-select>
				</b-form-group>
			</b-form>
		</div>

		<div v-else>
			<b-alert v-if="exists(selectedApplication)" :show="true" dismissible @dismissed="application = null">
				You are requesting an additional insured be added to your existing application. <br /> <strong>{{ selectedApplication.name_of_insured }} - {{ selectedApplication.app_id }}</strong>
			</b-alert>
			<template v-if="!addingNew && !editingExisting && length(certificate_holders)">
				<div class="d-md-flex">
					<b-form-group
						class="mb-0 pb-0"
						:label="addAnotherLabel + ` Existing`"
						label-for="certificate_holder"
						description="Existing Certificate holders are previously used records, we save them for your convenience. If you've added this insured before, you do not need to enter all the information again.">
						<v-select v-model="certificate_holder"
							label="certificate_holder"
							placeholder="Find a previous Certificate Holder"
							:filterable="false"
							:options="certificate_holders"
							@search="onSearch">
							<template slot="option" slot-scope="option">
								<div class="d-center">
									{{ option.name }}
								</div>
							</template>
							<template slot="selected-option" slot-scope="option">
								<div class="selected d-center">
									{{ option.name }}
								</div>
							</template>
						</v-select>
					</b-form-group>
					<div class="text-md-right pt-4 ml-md-2 text-nowrap" style="margin-top: 8px;">
						<b-button :disabled="! certificate_holder" :variant="buttonVariantCH" @click="addCH">
							<template v-if="newApplication">Add to List</template>
							<template v-else>Add to Request</template>
						</b-button>
					</div>
				</div>
				<div class="my-1 text-grey">- or -</div>
			</template>

			<div :class="{'mb-4': length(additionally_insured) }">
				<b-button v-if="!addingNew && !editingExisting" @click="addingNew = true">{{ addAnotherLabel }} New</b-button>
				<b-form v-if="addingNew || editingExisting" id="request-ai-form" @submit.prevent>
					<b-form-group label="Name of Additional Insured" class="mb-3">
						<b-form-input
							id="name"
							v-model.trim="form.name"
							autocomplete="off"
							required></b-form-input>
					</b-form-group>

					<fieldset class="form-group">
						<legend class="bv-no-focus-ring col-form-label pt-0">Address</legend>
						<formulate-address :context="form.address" />
					</fieldset>

					<b-form-group class="mt-3" label="Relationship">
						<b-form-select v-if="length(relationshipOptions)"
							v-model="form.relationship"
							:options="relationshipOptions" />
						<b-form-input v-else
							id="relationship"
							v-model.trim="form.relationship"
							autocomplete="off"
							required></b-form-input>
					</b-form-group>

					<b-form-group v-if="form.covered"
						label="Covered Party"
						class="mb-3"
						:description="'(optional) Enter the ' + form.covered.prefix + ' name that should be used on the certificate of insurance.'">
						<b-input-group>
							<template #prepend>
								<b-dropdown text="Select" variant="secondary">
									<template #button-content>
										<span class="text-capitalize">{{ form.covered.prefix }}</span>
									</template>
									<b-dropdown-item @click="form.covered.prefix = 'association'">Association</b-dropdown-item>
									<b-dropdown-item @click="form.covered.prefix = 'team'">Team</b-dropdown-item>
								</b-dropdown>
							</template>
							<b-form-input
								id="name"
								v-model.trim="form.covered.label"
								autocomplete="off"
								required></b-form-input>
						</b-input-group>
					</b-form-group>

					<template v-if="isStaff">
						<b-form-group v-if="length(additionallyInsured)"
							class="mt-3"
							label="Status"
							description="Approving or Denying from here will still trigger the email notifications.">
							<b-form-select v-model="form.approved"
								:options="approvalOptions" />
						</b-form-group>

						<b-form-group
							label="Description"
							description="This will be inserted into generated COI PDFs."
							label-for="description">
							<b-form-textarea
								id="description"
								v-model="form.description"
								name="description"></b-form-textarea>
						</b-form-group>
					</template>

					<template v-if="length(additionallyInsured)">
						<b-button variant="primary" @click="handleSubmit">Save Changes</b-button>
					</template>
					<template v-else>
						<p class="mb-2">
							<small>
								You can request one or more Additional Insured. Add it to your request and submit once you've added them all.
							</small>
						</p>

						<b-button :disabled="!isValid" :variant="buttonVariant" @click="addAI">
							<template v-if="newApplication">Add to List</template>
							<template v-else-if="editingExisting">Update</template>
							<template v-else>Add to Request</template>
						</b-button>
						<b-button @click="addingNew = false; editingExisting = false">Cancel</b-button>
					</template>
				</b-form>
			</div>

			<template v-if="!addingNew && !editingExisting && length(additionally_insured)">
				<h5>
					<template v-if="newApplication">Additional Insured to Add:</template>
					<template v-else>Additional Insured to Request:</template>
				</h5>
				<b-table
					ref="AIRequestTable"
					hover
					outlined
					dark
					:items="additionally_insured"
					:fields="fields"
					:current-page="currentPage"
					:per-page="perPage"
					stacked="md">
					<template #cell(name)="{ item }">
						<div>{{ item.name }}</div>
					</template>
					<template #cell(address)="{ item }">
						<div v-if="item.address">
							<strong>{{ item.address }}</strong><br />
							{{ item.city }}
							{{ item.state }},
							{{ item.zip }},
							{{ item.country }}
						</div>
						<em v-else class="text-grey">No Address</em>
					</template>
					<template #cell(relationship)="{ item }">
						<div v-if="relationshipOptions[item.relationship]">
							{{ relationshipOptions[item.relationship] }}
						</div>
						<div v-else>{{ item.relationship }}</div>
					</template>
					<template #cell(actions)="data">
						<b-button-group>
							<b-button
								v-b-tooltip.hover
								title="Edit"
								variant="warning"
								size="sm"
								@click="editAI(data.index)">
								<font-awesome-icon :icon="['fa', 'pen']" />
							</b-button>
							<b-button
								v-b-tooltip.hover
								title="Delete"
								variant="danger"
								size="sm"
								@click="deleteAI(data.index)">
								<font-awesome-icon :icon="['fas', 'trash']" />
							</b-button>
						</b-button-group>
					</template>
				</b-table>
				<b-button variant="primary" @click="handleSubmit">
					<template v-if="newApplication">Add</template>
					<template v-else-if="isStaff">Add</template>
					<template v-else>Submit Request</template>
					({{ length(additionally_insured) }})
				</b-button>
			</template>
		</div>
	</div>
</template>
<script>
import 'vue-select/dist/vue-select.css'
import vSelect from 'vue-select'
import FormulateAddress from '@/Formulate/Components/Address'
import api from '@/lib/api'
import auth from '@/Mixin/auth'
import store from '@/store'
import _ from 'lodash'
import formatDate from '@/lib/formatDate'

export default {
	components: { FormulateAddress, vSelect },
	mixins: [ auth ],
	props: {
		additionallyInsured: { // editing record
			type: Object,
			default: function () {
				return {}
			}
		},
		newApplication: { // This toggle is for new applications
			type: Boolean,
			default: false
		},
		searchParams: { // Extra params to add to the API request
			type: Object,
			default: () => {}
		}
	},
	data () {
		return {
			addingNew: false,
			editingExisting: false,
			existingEditIndex: null,
			application: this.$page.props.application,
			applications: [],
			selectedApplication: null,
			certificate_holder: null,
			certificate_holders: [], // belonging to current user
			additionally_insured: [],
			loading: false,
			fields: [
				{
					label: 'Contact Name',
					key: 'name',
					class: 'max-width'
				},
				{
					label: 'Address',
					key: 'address'
				},
				{
					label: 'Relationship',
					key: 'relationship'
				},
				{
					label: '',
					key: 'actions',
					class: 'text-right'
				}
			],
			totalRows: this.length(this.additionally_insured),
			currentPage: 1,
			perPage: 99,
			pageOptions: [5, 10, 15, { value: 100, text: 'Show a lot' }],
			approvalOptions: [
				{ value: null, text: 'Pending' },
				{ value: true, text: 'Approved' },
				{ value: false, text: 'Denied' }
			],
			relationshipOptions: {},
			form: {
				approved: '',
				name: '',
				address: {
					model: {
						address: null,
						country: null,
						state: null,
						city: null,
						zip: null
					},
					attributes: {
						'all-countries': undefined
					}
				},
				covered: { prefix: 'association', label: '' },
				relationship: null,
				description: null
			}
		}
	},
	computed: {
		isValid () {
			if (! this.form.name) return false
			if (! this.form.relationship) return false
			if (! this.form.address.model.address) return false
			if (! this.form.address.model.city) return false
			if (! this.form.address.model.state) return false
			if (! this.form.address.model.zip) return false
			if (! this.form.address.model.country) return false

			return true
		},
		buttonVariant () {
			return this.isValid ? 'success': 'secondary'
		},
		buttonVariantCH () {
			return !! this.certificate_holder ? 'success' : 'secondary'
		},
		addAnotherLabel () {
			if (this.length(this.additionally_insured)) {
				return 'Add Another'
			}

			return 'Add'
		},
		userId () {
			if (this.isStaff && this.application) {
				return this.application.creator.id
			}

			return this.auth.id
		},
		aiApiRoute () {
			if (['admin', 'manager', 'employee', 'developer'].includes(this.auth.type)) {
				return window.route('admin.users.ai.api.index', this.userId)
			}

			return window.route('ai.index')
		}
	},
	watch: {
		selectedApplication (newValue) {
			if (this.length(newValue)) {
				this.application = newValue
				this.getCertificateHolders()
				this.$store.commit('setFormInfo', newValue.form)
				this.relationshipOptions = store.getters.getFormFieldOptions('relationship') ?? {}
			} else {
				this.application = null
			}
		},
		'form.covered.prefix' (newValue) {
			if (newValue !== undefined) {
				localStorage.setItem('ai.prefix', newValue)
			}
		}
	},
	mounted () {
		if (this.length(this.application)) {
			this.getCertificateHolders()
			this.$store.commit('setFormInfo', this.application.form)
			this.relationshipOptions = store.getters.getFormFieldOptions('relationship') ?? {}
		} else {
			const today = new Date()
			let twoYearsFromToday = new Date()
			twoYearsFromToday.setFullYear(today.getFullYear() + 2)

			const url = window.route('applications')

			api({
				method: 'POST',
				url,
				data: {
					'status': 6, // Policy Bound
					'has_invoice': 'yes',
					'date_field': 'policy_expiration_date',
					'date_range': {
						start: formatDate(today),
						end: formatDate(twoYearsFromToday)
					},
					'json': true.valueOf(),
					...this.searchParams
				}
			}).then((response) => {
				this.applications = response.data
			})
		}

		if (this.length(this.additionallyInsured)) {
			this.addingNew = true
			this.form.approved = this.additionallyInsured.approved
			this.form.name = this.additionallyInsured.name

			this.form.covered = this.additionallyInsured.covered === null
				? { prefix: 'association', label: '' }
				: this.additionallyInsured.covered

			this.form.relationship = this.additionallyInsured.relationship
			this.form.description = this.additionallyInsured.description
			this.form.address.model.address = this.additionallyInsured.address
			this.form.address.model.country = this.additionallyInsured.country
			this.form.address.model.state = this.additionallyInsured.state
			this.form.address.model.city = this.additionallyInsured.city
			this.form.address.model.zip = this.additionallyInsured.zip
		}

		if (this.form.covered) {
			const aiPrefix = localStorage.getItem('ai.prefix')
			if (aiPrefix !== null) {
				this.form.covered.prefix = aiPrefix
			} else {
				localStorage.setItem('ai.prefix', 'association')
			}
		}
	},
	methods: {
		getCertificateHolders () {
			const url = this.aiApiRoute
			const method = 'POST'
			const data = { 'json': true, 'user_id': this.userId }

			api({
				method,
				url,
				data
			}).then((response) => {
				if (this.length(response.data)) {
					this.certificate_holders = response.data
				} else {
					this.addingNew = true
				}
			})
		},
		resetForm () {
			this.form = {
				name: '',
				address: {
					model: {},
					attributes: {
						'all-countries': undefined
					}
				},
				covered: { prefix: 'association', label: '' },
				relationship: null,
				description: null
			}

			this.addingNew = false
			this.editingExisting = false
			this.existingEditIndex = null
		},
		addAI () {
			const aiRecord = {
				name: this.form.name,
				address: this.form.address.model.address,
				city: this.form.address.model.city,
				state: this.form.address.model.state,
				zip: this.form.address.model.zip,
				country: this.form.address.model.country,
				covered: this.form.covered,
				relationship: this.form.relationship,
				description: this.form.description
			}

			if (this.addingNew) {
				this.additionally_insured.push(aiRecord)
			} else {
				this.additionally_insured[this.existingEditIndex] = aiRecord
			}

			this.resetForm()
		},
		editAI (index) {
			const existingRecord = this.additionally_insured[index]

			this.form.name = existingRecord.name
			this.form.address.model.address = existingRecord.address
			this.form.address.model.city = existingRecord.city
			this.form.address.model.state = existingRecord.state
			this.form.address.model.zip = existingRecord.zip
			this.form.address.model.country = existingRecord.country
			this.form.covered = this.form.covered ?? { prefix: 'association', label: '' }
			this.form.relationship = existingRecord.relationship
			this.form.description = existingRecord.description ?? null

			this.existingEditIndex = index
			this.editingExisting = true
		},
		addCH () {
			this.additionally_insured.push({
				name: this.certificate_holder.name,
				address: this.certificate_holder.address,
				city: this.certificate_holder.city,
				state: this.certificate_holder.state,
				zip: this.certificate_holder.zip,
				country: this.certificate_holder.country,
				covered: this.certificate_holder.covered,
				relationship: this.certificate_holder.relationship,
				description: this.certificate_holder.description
			})

			this.certificate_holder = null
		},
		deleteAI (index) {
			this.$delete(this.additionally_insured, index)
			if (this.$refs.AIRequestTable) {
				this.$refs.AIRequestTable.refresh()
			}
		},

		onSearch (search, loading) {
			if(search.length) {
				loading(true)
				this.search(loading, search, this)
			}
		},
		search: _.debounce((loading, search, vm) => {
			const url = this.aiApiRoute
			const method = 'POST'
			const data = { 'search': search, 'json': true, 'user_id': vm.userId }

			api({
				method,
				url,
				data
			}).then((response) => {
				vm.certificate_holders = response.data
				loading(false)
			}).catch(err => {
				console.error(err)
			})
		}, 350),

		handleCreate () {
			let prefix = ''

			if (this.isStaff) {
				prefix = 'admin.'
			}

			const url = this.route(prefix + 'ai.store', this.application.id)
			const data = this.additionally_insured

			this.$inertia.post(url, data, {
				preserveScroll: true,
				preserveState: true,
				only: ['additionally_insured', 'requested_additionally_insured', 'flash'],
				onFinish: () => this.$emit('submitted')
			})
		},
		handleEdit () {
			let url

			if (this.isStaff) {
				// admin is editing
				url = this.route('admin.ai.update', this.additionallyInsured.id)
			} else if (this.application) {
				// record is related to the application
				url = this.route('application-ai.update', this.additionallyInsured.id)
			} else {
				// record is related to the user
				url = this.route('ai.update', this.additionallyInsured.id)
			}

			const data = {
				name: this.form.name,
				approved: this.form.approved,
				address: this.form.address.model.address,
				country: this.form.address.model.country,
				state: this.form.address.model.state,
				city: this.form.address.model.city,
				zip: this.form.address.model.zip,
				covered: this.form.covered,
				relationship: this.form.relationship,
				description: this.form.description
			}

			this.$inertia.put(url, data, {
				preserveScroll: true,
				preserveState: true,
				only: ['additionally_insured', 'requested_additionally_insured', 'flash'],
				onFinish: () => this.$emit('submitted')
			})
		},
		handleSubmit () {
			if (this.length(this.additionallyInsured)) {
				this.handleEdit()
			} else {
				this.handleCreate()
			}
		},

		onAppSearch (search, loading) {
			if(search.length) {
				loading(true)
				this.appSearch(loading, search, this)
			}
		},
		appSearch: _.debounce((loading, search, vm) => {
			const today = new Date()
			let twoYearsFromToday = new Date()
			twoYearsFromToday.setFullYear(today.getFullYear() + 2)

			const url = window.route('applications', {
				'search': search,
				'status': 6, // Policy Bound
				'json': true,
				'has_invoice': 'yes',
				'date_field': 'policy_expiration_date',
				'date_range': {
					start: formatDate(today),
					end: formatDate(twoYearsFromToday)
				},
				...vm.searchParams
			})

			api({
				method: 'GET',
				url,
				data: {}
			}).then((response) => {
				vm.applications = response.data
				loading(false)
			})
		}, 350)
	}
}
</script>
<style>
#request-ai ul { margin-bottom: 0; }
</style>
